import axios from 'src/api/Api';
import {
    GET_CONSIGNED_INVENTORY_SUCCESS,
    GET_CONSIGNED_INVENTORY_FAILURE,
    GET_CONSIGNED_PREFERENCE_SUCCESS,
    GET_CONSIGNED_PREFERENCE_FAILURE,
    FETCH_FILTER_DATA_SUCCESS,
    FETCH_FILTER_DATA_FAILURE,
} from '../actions';

import { call, put } from 'redux-saga/effects';
import { errorHandler } from 'src/errorHandler/errorHandler';
import { logUiErrorToBackend } from 'src/utils/logUiErrorToBackend';
import { accountNumberZeroTrimmer } from 'src/utils/util';

const defaultConsignedInventoryParams = {
    resourceType: 'CONSIGN_INVENTORY_LIST',
    currentPage: 0,
    pageSize: 50,
    sortOrder: 'ASC',
    orderType: 'ZLC',
    sortCode: 'account',
};

const normalizeConsignedInventoryParams = (payload = {}) => {
    const params = payload || {};
    return {
        ...defaultConsignedInventoryParams,
        ...params,
        'item description':
            params['item description'] ??
            params.itemDescription,
        'item number':
            params['item number'] ??
            params.itemNumber,
    };
};

const buildQueryString = (params) =>
    Object.entries(params)
        .filter(
            ([key, value]) =>
                key !== 'itemDescription' &&
                key !== 'itemNumber' &&
                value !== undefined &&
                value !== null &&
                value !== ''
        )
        .map(
            ([key, value]) =>
                `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
        )
        .join('&');

export const buildConsignedInventoryUrl = (baseSiteId, payload) => {
    const params = normalizeConsignedInventoryParams(payload);
    return `occ/v2/${baseSiteId}/consignment/inventory?${buildQueryString(
        params
    )}`;
};

export default function* getConsignedInventory(action) {
    const baseSiteId = localStorage.getItem('baseSiteId');
    const url = buildConsignedInventoryUrl(baseSiteId, action.payload);

    try {
        const { data: result } = yield call(axios, {
            method: 'get',
            url,
        });
        yield put({
            type: GET_CONSIGNED_INVENTORY_SUCCESS,
            payload: result,
        });
    } catch (error) {
        logUiErrorToBackend(error, {
            apiEndPoint: url,
            requestPayload: action.payload || {},
        });
        yield put({
            type: GET_CONSIGNED_INVENTORY_FAILURE,
            payload: errorHandler('503'),
        });
    }
}

export function* getConsignedPreference(action) {
    const baseSiteId = localStorage.getItem('baseSiteId');
    const url = `occ/v2/${baseSiteId}/inventory/getInventoryPreference`;

    try {
        const { data: result } = yield call(axios, {
            method: 'get',
            url,
        });
        yield put({
            type: GET_CONSIGNED_PREFERENCE_SUCCESS,
            payload: result,
        });
    } catch (error) {
        logUiErrorToBackend(error, {
            apiEndPoint: url,
            requestPayload: action.payload || {},
        });
        yield put({
            type: GET_CONSIGNED_PREFERENCE_FAILURE,
            payload: errorHandler('503'),
        });
    }
}

const FILTER_API_ENDPOINTS = {
    districtFilters: `/aardt/districts`,
    territoryFilters: `/aardt/territories`,
    accountFilters: `/aardt/accounts?orderType=null`,
    consignedBrandFilters: `/consignment/consignInventoryBrandList`,
};

/**
 * Transform API response to standard filter format (filterCode, filterName)
 * Maps different API field names to consistent filter structure
 */
const transformFilterResponse = (filterKey, data) => {
    //if (!Array.isArray(data)) return data;
    
    switch (filterKey) {
        case 'accountFilters':
            const accounts = data.accounts || [];
            accounts.sort((a, b) => a.accountName.localeCompare(b.accountName));
            if (accounts.length > 1) {
                accounts.unshift({
                    accountId: 'All',
                    accountName: 'All',
                });
            }
            return accounts.map(item => ({
                filterCode: item.accountId,
                filterName: item.accountId === 'All'
                    ? 'All'
                    : `${item.accountName.length > 20 ? `${item.accountName.slice(0, 20)}...` : item.accountName} ${accountNumberZeroTrimmer(item.accountId)}`,
                ...item, // preserve original fields 
            }));
        case 'districtFilters':
            // TODO: Update field names based on actual API response
            const districts = data.districts || [];
            if (districts.length > 1) {
                districts.unshift({
                    districtCode: 'All',
                    districtName: 'All', 
                });
            }
            return districts.map(item => ({
                filterCode: item.districtCode,
                filterName: item.districtName,
                ...item,
            }));
        case 'territoryFilters':
            // TODO: Update field names based on actual API response
            const territories = data.territories || [];
            if (territories.length > 1) {
                territories.unshift({
                    terrirotryCode: 'All',
                    terrirotryName: 'All',
                });
            }
            return territories.map(item => ({
                filterCode: item.terrirotryCode,
                filterName: item.terrirotryName,
                ...item,
            }));
        case 'consignedBrandFilters':
            const brands = data.brandList || [];
            return brands.map(item => ({
                filterCode: item.brandCode,
                filterName: item.brandName,
                ...item,
            }));
        default:
            return data;
    }
};

export function* fetchFilterData(action) {
    const { filterKey, params, scopeKey } = action.payload;
    const baseSiteId = localStorage.getItem('baseSiteId');
    const endpoint = FILTER_API_ENDPOINTS[filterKey];
    
    if (!endpoint) {
        yield put({
            type: FETCH_FILTER_DATA_FAILURE,
            payload: { filterKey, error: `Unknown filter key: ${filterKey}` },
        });
        return;
    }

    const url = `occ/v2/${baseSiteId}${endpoint}`;

    try {
        const { data: result } = yield call(axios, {
            method: 'get',
            url,
            params,
        });
        const transformedData = transformFilterResponse(filterKey, result);
        yield put({
            type: FETCH_FILTER_DATA_SUCCESS,
            payload: { filterKey, data: transformedData, scopeKey },
        });
    } catch (error) {
        logUiErrorToBackend(error, {
            apiEndPoint: url,
            requestPayload: params || {},
        });
        yield put({
            type: FETCH_FILTER_DATA_FAILURE,
            payload: { filterKey, error: errorHandler('503'), scopeKey },
        });
    }
}